import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { format } from 'date-fns';

import { LICENSE_MODULES, LICENSE_TYPES } from 'interfaces/license.interface';
import { ICreateLicenseDto } from 'interfaces/license.interface';

interface ICreateLicenseForm {
  clientId: string;
  type: string;
  maxEndpoints: number;
  startDate: Date | null;
  endDate: Date | null;
  modules: string[];
  hwid: string;
  backendUrl: string;
}
import { createLicense } from 'requests/license.request';
import { markInvalidFields } from 'utils/form.utils';

import Input from 'components/input/input.component';
import Select from 'components/select/select.component';
import DatePickerComponent from 'components/date-picker/date-picker.component';
import { Checkbox } from 'components/checkbox/checkbox.component';
import Button from 'components/button/button.component';
import Loader from 'components/loader/loader.component';

interface IProps {
  onSuccess: () => void;
}

const CreateLicenseModal: React.FC<IProps> = ({ onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object().shape({
    clientId: Yup.string().required(t('validation.required') as string),
    type: Yup.string().required(t('validation.required') as string),
    maxEndpoints: Yup.number()
      .min(1, t('validation.minValue', { value: 1 }) as string)
      .required(t('validation.required') as string),
    startDate: Yup.date().nullable().required(t('validation.required') as string),
    endDate: Yup.date().nullable().required(t('validation.required') as string),
    modules: Yup.array()
      .of(Yup.string())
      .min(1, t('validation.minModules') as string)
      .required(t('validation.required') as string),
    // Not required: a licence can exist before the customer's server does. It is
    // required before an agent installer can be stamped, which the table surfaces.
    backendUrl: Yup.string()
      .trim()
      .matches(/^(https?:\/\/)?[A-Za-z0-9._-]+(:\d+)?\/?$/, {
        message: t('validation.invalidUrl') as string,
        // Without this an empty value fails the pattern, which would quietly make an
        // optional field mandatory.
        excludeEmptyString: true,
      })
      .optional(),
    hwid: Yup.string().optional(),
  });

  const formik = useFormik<ICreateLicenseForm>({
    initialValues: {
      clientId: '',
      type: '',
      maxEndpoints: 1,
      startDate: null,
      endDate: null,
      modules: [],
      hwid: '',
      backendUrl: '',
    },
    validationSchema,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => { /* handled by handleSubmit */ },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await formik.validateForm();
    if (Object.keys(formik.errors).length > 0) {
      markInvalidFields(formik, 'licenses');
      return;
    }

    setLoading(true);
    const dto: ICreateLicenseDto = {
      clientId: formik.values.clientId,
      type: formik.values.type,
      maxEndpoints: formik.values.maxEndpoints,
      startDate: formik.values.startDate ? format(formik.values.startDate, 'yyyy-MM-dd') : '',
      endDate: formik.values.endDate ? format(formik.values.endDate, 'yyyy-MM-dd') : '',
      modules: formik.values.modules,
      hwid: formik.values.hwid || undefined,
      backendUrl: formik.values.backendUrl?.trim() || undefined,
    };

    createLicense(dto)
      .then(() => {
        toast.success(t('licenses.createSuccess'));
        onSuccess();
      })
      .catch(() => toast.error(t('licenses.createError')))
      .finally(() => setLoading(false));
  };

  const handleChange = ({ target: { name, value } }: React.ChangeEvent<HTMLInputElement>) => {
    void formik.setFieldValue(name, value, true);
  };

  const handleSelectChange = ({
    target: { name, value },
  }: React.ChangeEvent<HTMLSelectElement>) => {
    void formik.setFieldValue(name, value, true);
  };

  const handleModuleToggle = (module: string) => {
    const current = formik.values.modules;
    if (current.includes(module)) {
      void formik.setFieldValue(
        'modules',
        current.filter((m) => m !== module),
        true,
      );
    } else {
      void formik.setFieldValue('modules', [...current, module], true);
    }
  };

  const typeOptions = LICENSE_TYPES.map((type) => ({ name: type, value: type }));

  return (
    <Loader loading={loading}>
      <FormikProvider value={formik}>
        <form onSubmit={handleSubmit} noValidate>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <Input
              formik={formik}
              name="clientId"
              value={formik.values.clientId}
              label={t('licenses.clientId')}
              onChange={handleChange}
            />
            <Select
              name="type"
              options={typeOptions}
              valueKey="value"
              labelKey="name"
              formik={formik}
              label={t('licenses.type')}
              value={formik.values.type}
              onChange={handleSelectChange}
            />
            <Input
              formik={formik}
              name="maxEndpoints"
              type="number"
              value={String(formik.values.maxEndpoints)}
              label={t('licenses.maxEndpoints')}
              onChange={handleChange}
            />
            <DatePickerComponent
              name="startDate"
              value={formik.values.startDate}
              formik={formik}
              label={t('licenses.startDate')}
              onChange={(date) => formik.setFieldValue('startDate', date, true)}
            />
            <DatePickerComponent
              name="endDate"
              value={formik.values.endDate}
              formik={formik}
              label={t('licenses.endDate')}
              onChange={(date) => formik.setFieldValue('endDate', date, true)}
            />
            <div>
              <label style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                {t('licenses.modules')}
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                {LICENSE_MODULES.map((module) => (
                  <Checkbox
                    key={module}
                    name={`module_${module}`}
                    label={module}
                    checked={formik.values.modules.includes(module)}
                    onChange={() => handleModuleToggle(module)}
                  />
                ))}
              </div>
            </div>
            <Input
              formik={formik}
              name="backendUrl"
              value={formik.values.backendUrl || ''}
              label={t('licenses.backendUrl')}
              placeholder="http://10.100.67.70"
              onChange={handleChange}
            />
            <Input
              formik={formik}
              name="hwid"
              value={formik.values.hwid || ''}
              label={t('licenses.hwid')}
              onChange={handleChange}
            />
          </div>
          <div className="modal__footer">
            <Button type="submit" primary>
              {t('licenses.create')}
            </Button>
          </div>
        </form>
      </FormikProvider>
    </Loader>
  );
};

export default CreateLicenseModal;
